import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { Box, Chip, MenuItem, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import { IconDownload, IconPlus, IconUsers } from "@tabler/icons";
import PageHeader from "src/components/shared/PageHeader";
import ResourceList from "src/components/shared/ResourceList";
import PersonAvatar from "src/components/shared/PersonAvatar";
import ActionButton from "src/components/shared/ActionButton";
import { formatDate, plural } from "src/components/shared/format";
import CustomerPanel, { EmailLine, customerName } from "./CustomerPanel";
import { money } from "src/pages/cases/format";
import { csvName, downloadCsv } from "src/pages/cases/csv";
import { customersCsv } from "./customersCsv";
import DynamicModal from "src/components/modal/DynamicModal";
import { CommonAlert } from "src/components/alert/CommonAlert";
import PageContainer from "src/components/container/PageContainer";
import { dataLocalStorage, getLocalStorage, inputType, isValidArray } from "src/helper/publicFunction";
import { ManageClients, DeleteClients, EditClients, SearchClients, GetClients, SetTestAccounts } from "./actions";
import { undoNote } from "src/pages/catalogue/helpers";

// Customers: the people with a Safrati account on the website. Each row is a
// person (photo or initials, name, email) with what they paid and owe and
// their sign-in switch; the row opens the customer in a side panel with
// their own address (?customer=4; CMS review rania-23). The API pages,
// searches, filters (everyone, real customers, test accounts, with
// applications) and sorts the list; the search, filter and sort are in the
// address too. Test accounts are marked one by one in the panel or in bulk
// (rania-13, nadia-18). An older API pages only, and its search is a
// separate call that returns every match at once, so pages are off then.

const PRIVILEGE = "3";

const SHOWS = [
  { key: "all", label: "Everyone" },
  { key: "real", label: "Real customers" },
  { key: "test", label: "Test accounts" },
  { key: "with_applications", label: "With applications" },
];
const ORDERS = [
  { key: "oldest", label: "Oldest account first" },
  { key: "newest", label: "Newest account first" },
  { key: "name", label: "Name, A to Z" },
  { key: "applications", label: "Most applications" },
  { key: "last_application", label: "Latest application" },
  { key: "owed", label: "Owes the most" },
];

const Clients = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const alert = CommonAlert();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const query = (params.get("q") || "").trim();
  const [search, setSearch] = useState(query);
  const show = SHOWS.some((x) => x.key === params.get("show")) ? params.get("show") : "all";
  const sort = ORDERS.some((x) => x.key === params.get("sort")) ? params.get("sort") : "oldest";
  const customerId = /^\d+$/.test(params.get("customer") || "") ? Number(params.get("customer")) : null;
  const [panel, setPanel] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busyTest, setBusyTest] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadingEditdatatype, setloadingEditdatatype] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [values, setvalues] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const ticket = useRef(0);
  // Below md the list is cards: the name and email get the card's width, and
  // the sign-in switch moves to a row of its own under them.
  const cardsShown = useMediaQuery((t) => t.breakpoints.down("md"));
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const can = (right, key = PRIVILEGE) => privileges[key]?.[right] == 1; // eslint-disable-line eqeqeq

  const Schema = Yup.object().shape({
    first_name: Yup.string().trim().required("Enter their first name."),
    last_name: Yup.string().trim().required("Enter their last name."),
    email: Yup.string().trim().required("Enter their email address.").email("Enter an email address like name@example.com."),
    password: editMode ? Yup.string().trim() : Yup.string().trim().required("Choose a password for their account."),
  });

  // The address holds the search, filter, sort and open customer.
  const setParam = useCallback(
    (changes, { push = false } = {}) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === "" || value == null) next.delete(key);
        else next.set(key, String(value));
      });
      setParams(next, { replace: !push });
    },
    [params, setParams],
  );

  // One loader; a newer request wins. The API filters, sorts and searches
  // (it answers meta.counts); an older one pages only, so its search is
  // the separate search call.
  const load = useCallback(() => {
    const mine = ++ticket.current;
    setLoadingData(true);
    setLoadError(false);
    const done = (res, searched) => {
      if (mine !== ticket.current) return;
      if (res === false) {
        setLoadError(true);
        setData([]);
        setTotal(0);
      } else {
        const rows = isValidArray(res?.data) ? res.data : [];
        setData(rows);
        setTotal(searched ? rows.length : res?.meta?.total ?? rows.length);
        if (!searched) setMeta(res?.meta || null);
      }
      setLoadingData(false);
    };
    dispatch(
      GetClients({
        page,
        rowsPerPage,
        search: query,
        show,
        sort,
        callback: (res) => {
          if (res && query && res?.meta?.counts === undefined) dispatch(SearchClients({ search: query, callback: (found) => done(found, true) }));
          else done(res, false);
        },
      }),
    );
  }, [dispatch, page, rowsPerPage, query, show, sort]);

  useEffect(() => {
    load();
  }, [load]);

  // Search a beat after typing stops.
  useEffect(() => {
    const value = search.trim();
    if (value === query) return undefined;
    const timer = setTimeout(() => {
      setPage(0);
      setParam({ q: value });
    }, 400);
    return () => clearTimeout(timer);
  }, [search, query, setParam]);

  // The open customer follows the address: from the list when it is there,
  // else asked for by id (a link from a colleague).
  useEffect(() => {
    if (!customerId) {
      setPanel(null);
      return;
    }
    const row = data.find((r) => Number(r.id) === customerId);
    if (row) {
      setPanel(row);
      return;
    }
    if (panel && Number(panel.id) === customerId) return;
    dispatch(
      GetClients({
        id: customerId,
        page: 0,
        rowsPerPage: 1,
        callback: (res) => {
          const found = isValidArray(res?.data) ? res.data.find((r) => Number(r.id) === customerId) : null;
          if (found) setPanel(found);
          else if (res !== false) {
            toast.error(`Customer #${customerId} isn’t in the list. They may have been deleted.`);
            setParam({ customer: "" });
          }
        },
      }),
    );
    // panel: only to skip asking again for the one already open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, data, dispatch]);

  const openCustomer = (row) => {
    setPanel(row);
    setParam({ customer: row.id }, { push: true });
  };
  const closeCustomer = () => {
    setPanel(null);
    setParam({ customer: "" });
  };

  const fields = useMemo(
    () => [
      { failed: "id", type: inputType.text, title: "id", fullwidth: true, customdesign: { display: "none" } },
      {
        failed: "first_name",
        type: inputType.text,
        title: "First name",
        fullwidth: true,
        customdesign: { width: "100%" },
        child: { failed: "last_name", type: inputType.text, title: "Last name", fullwidth: true, customdesign: { width: "100%" } },
      },
      { failed: "email", type: inputType.email, title: "Email", fullwidth: true },
      {
        failed: "password",
        type: inputType.password,
        title: editMode ? "New password (optional: leave empty to keep theirs)" : "Password",
        fullwidth: true,
      },
      {
        failed: "image",
        type: inputType.image,
        title: "Photo (optional)",
        fullwidth: true,
        fileTypes: ["JPG", "PNG", "JPEG", "WEBP"],
        multiple: false,
        label: "Drop a photo here, or click to choose one (JPG, PNG or WEBP)",
        fileOrFiles: "File",
      },
    ],
    [editMode],
  );

  const nameOf = customerName;

  const confirmDelete = (rows, done) =>
    alert.show({
      title: rows.length === 1 ? `Delete ${nameOf(rows[0])}?` : `Delete ${rows.length} customers?`,
      message: `Their account goes, and they can no longer sign in. ${undoNote("customer")}`,
      type: "delete",
      actions: [
        { name: "Cancel", type: "cancel" },
        {
          name: rows.length === 1 ? "Delete" : `Delete ${rows.length}`,
          type: "delete",
          callback: () =>
            dispatch(
              DeleteClients({
                id: rows.map((r) => r.id),
                callback: (res) => {
                  if (res) {
                    done?.();
                    closeCustomer();
                  }
                  load();
                },
              }),
            ),
        },
      ],
    });

  const edit = (row) => {
    setEditMode(true);
    setvalues(row);
    setShowModal(true);
  };

  // Sign-in on/off. Switching off locks the customer out of their
  // applications, so it asks first; switching on is instant. Either way a
  // toast says what happened, and a failure leaves the switch as it was.
  const saveSignIn = (row, next) => {
    const name = nameOf(row);
    setloadingEditdatatype(`${row?.id}-is_active`);
    dispatch(
      EditClients({
        id: row?.id,
        dataFailed: next,
        nameFailed: "is_active",
        quiet: true,
        callback: (res) => {
          if (res) {
            setData((rows) => rows.map((item) => (item.id === row.id ? { ...item, is_active: next } : item)));
            setPanel((p) => (p && p.id === row.id ? { ...p, is_active: next } : p));
            toast.success(next ? `${name} can sign in again.` : `${name} can no longer sign in.`);
          } else {
            toast.error(`Couldn't change sign-in for ${name}. Try again.`);
          }
          setloadingEditdatatype("");
        },
      }),
    );
  };

  const setSignIn = (row, next) => {
    if (next) {
      saveSignIn(row, 1);
      return;
    }
    const name = nameOf(row);
    alert.show({
      title: `Stop ${name} signing in?`,
      message: "They won't be able to open their applications until you switch this back on.",
      type: "delete",
      actions: [
        { name: "Cancel", type: "cancel" },
        { name: "Stop sign-in", type: "delete", callback: () => saveSignIn(row, 0) },
      ],
    });
  };

  const active = (r) => r.is_active == 1 || r.is_active === true; // eslint-disable-line eqeqeq
  const testReady = meta?.test_flag_ready === true;

  // Test accounts, one or many (migration 025). The toast says who changed
  // and what it does to the numbers; a failure says nothing changed.
  const markTest = async (rows, next, done) => {
    const who = rows.length === 1 ? nameOf(rows[0]) : `${rows.length} customers`;
    setBusyTest(true);
    try {
      const res = await dispatch(SetTestAccounts({ ids: rows.map((r) => r.id), isTest: next }));
      const still = res?.data?.still_test_by_name || [];
      const ids = new Set(rows.map((r) => r.id));
      const update = (r) => (ids.has(r.id) ? { ...r, test_flag: next, is_test: next || still.includes(r.id) || !!r.test_by_name } : r);
      setData((list) => list.map(update));
      setPanel((p) => (p && ids.has(p.id) ? update(p) : p));
      if (next) toast.success(`${who} ${rows.length === 1 ? "is" : "are"} now marked as test accounts. With “Hide test accounts” on, their applications leave the counts on Home, Cases, Key numbers and Money.`);
      else toast.success(`${who} ${rows.length === 1 ? "is" : "are"} no longer marked as test accounts.${still.length ? ` ${still.length === 1 ? "1 stays one" : `${still.length} stay ones`} by name or email (zz…@example.com or “ZZ ”).` : ""}`);
      done?.();
      load();
    } catch (err) {
      toast.error(
        err?.status === 409
          ? "Marking test accounts needs database update 025. Nothing changed."
          : `Couldn’t change ${who}. Nothing changed. Try again.`,
      );
    } finally {
      setBusyTest(false);
    }
  };

  // Every customer under the search and filter, as a CSV file (rania-37).
  const exportCsv = () =>
    new Promise((resolve) => {
      setExporting(true);
      dispatch(
        GetClients({
          page: 0,
          rowsPerPage: 100,
          search: query,
          show,
          sort,
          exportAll: true,
          callback: (res) => {
            setExporting(false);
            if (res === false) toast.error("The export didn’t work. Nothing was downloaded. Try again.");
            else {
              const rows = isValidArray(res?.data) ? res.data : [];
              const all = res?.meta?.counts !== undefined;
              downloadCsv(csvName("Customers", show !== "all" ? SHOWS.find((x) => x.key === show).label : "", query ? "search" : ""), customersCsv(rows));
              if (!all && rows.length < (res?.meta?.total ?? rows.length)) toast.warn(`Exported ${rows.length} of ${res.meta.total}: this server sends one page at a time.`);
              else toast.success(`Exported ${plural(rows.length, "customer")}${query || show !== "all" ? ", with this search and filter" : ""}.`);
            }
            resolve();
          },
        }),
      );
    });

  return (
    <PageContainer title="Customers · Safrati CMS" description="People with a Safrati account">
      <PageHeader
        title="Customers"
        subtitle={total && !query ? `${plural(total, "person", "people")} have a Safrati account on the website.` : "People with a Safrati account on the website."}
        actions={
          <>
            {data.length > 0 && (
              <ActionButton variant="text" startIcon={<IconDownload size={16} />} busy={exporting} busyLabel="Exporting…" onClick={exportCsv}>
                Export CSV
              </ActionButton>
            )}
            {can("access_write") ? (
              <ActionButton
                variant="contained"
                startIcon={<IconPlus size={18} />}
                onClick={() => {
                  setvalues({});
                  setEditMode(false);
                  setShowModal(true);
                }}
              >
                Add a customer
              </ActionButton>
            ) : null}
          </>
        }
      />
      {/* On phones each fact under the name takes the full width, so
          "3 applications · last 21 Oct 2026" stays on one line. */}
      <Box sx={{ "& li dl": { gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(auto-fill, minmax(160px, 1fr))" } } }}>
        <ResourceList
          label="Customers"
          items={data}
          primaryLabel="Customer"
          primary={(r) => ({
            title: nameOf(r),
            subtitle: (
              <>
                {cardsShown ? <EmailLine email={r.email} /> : r.email}
                {r.is_test && <Chip size="small" label="Test account" sx={{ ml: cardsShown ? 0 : 1, mt: cardsShown ? 0.5 : 0, height: 22 }} />}
              </>
            ),
            avatar: <PersonAvatar name={nameOf(r)} src={r.image} size={40} />,
          })}
          columns={[
            // Applications per customer (clients/get application_count and
            // last_application_at). Until the API sends them the column is
            // left out rather than showing guesses.
            ...(data.some((r) => r.application_count !== undefined)
              ? [
                  {
                    key: "applications",
                    label: "Applications",
                    hideBelow: "md",
                    render: (r) => {
                      if (r.application_count === undefined || r.application_count === null) return null;
                      const count = Number(r.application_count) || 0;
                      if (!count)
                        return (
                          <Typography component="span" variant="body2" color="text.secondary">
                            No applications yet
                          </Typography>
                        );
                      // Each half keeps together; if they must wrap, it is at the "·".
                      return (
                        <span>
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            {plural(count, "application")}
                          </Box>
                          {r.last_application_at ? (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                              {` · last ${formatDate(r.last_application_at)}`}
                            </Typography>
                          ) : null}
                        </span>
                      );
                    },
                  },
                ]
              : []),
            // What they paid and owe (rania-23, georges-35).
            ...(data.some((r) => r.paid_total !== undefined)
              ? [
                  {
                    key: "money",
                    label: "Paid · owes",
                    hideBelow: "md",
                    render: (r) =>
                      r.paid_total === undefined ? null : (
                        <span style={{ whiteSpace: "nowrap" }}>
                          {money(r.paid_total)}
                          <Typography component="span" variant="body2" sx={{ color: r.owed_total ? "warning.dark" : "text.secondary", fontWeight: r.owed_total ? 600 : 400 }}>
                            {r.owed_total ? ` · owes ${money(r.owed_total)}` : " · owes nothing"}
                          </Typography>
                        </span>
                      ),
                  },
                ]
              : []),
            {
              key: "active",
              label: "Can sign in",
              width: 140,
              cardAside: !cardsShown,
              render: (r) => (
                <Switch
                  checked={active(r)}
                  disabled={!can("access_edit") || loadingEditdatatype === `${r.id}-is_active`}
                  onChange={() => setSignIn(r, active(r) ? 0 : 1)}
                  inputProps={{ role: "switch", "aria-label": `${nameOf(r)} can sign in` }}
                />
              ),
            },
          ]}
          cards={false}
          onOpen={openCustomer}
          openLabel={(r) => `Open ${nameOf(r)}`}
          rowActions={(r) => [
            { label: "Open", onClick: () => openCustomer(r) },
            can("access_edit") && { label: "Edit details", onClick: () => edit(r) },
            can("access_read", "23") && { label: "Ask Safrati chats", onClick: () => navigate(`/user-ai-chat-sessions/${r.id}`) },
            can("access_delete") && { label: "Delete", destructive: true, onClick: () => confirmDelete([r]) },
          ]}
          bulkActions={
            can("access_edit") || can("access_delete")
              ? [
                  ...(can("access_edit") && testReady
                    ? [
                        { label: "Mark as test accounts", onClick: (items, done) => markTest(items, true, done) },
                        { label: "Not test accounts", onClick: (items, done) => markTest(items, false, done) },
                      ]
                    : []),
                  ...(can("access_delete") ? [{ label: "Delete", destructive: true, onClick: (items, done) => confirmDelete(items, done) }] : []),
                ]
              : undefined
          }
          search={{ value: search, onChange: setSearch, placeholder: "Search by name or email" }}
          filters={
            meta?.counts ? (
              <>
                <TextField
                  select
                  size="small"
                  value={show}
                  onChange={(e) => {
                    setPage(0);
                    setParam({ show: e.target.value === "all" ? "" : e.target.value });
                  }}
                  SelectProps={{ SelectDisplayProps: { "aria-label": "Show" } }}
                  sx={{ minWidth: 200, "& .MuiInputBase-root": { minHeight: 40 } }}
                >
                  {SHOWS.map((x) => (
                    <MenuItem key={x.key} value={x.key}>
                      {x.label} ({meta.counts[x.key] ?? 0})
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  value={sort}
                  onChange={(e) => {
                    setPage(0);
                    setParam({ sort: e.target.value === "oldest" ? "" : e.target.value });
                  }}
                  SelectProps={{ SelectDisplayProps: { "aria-label": "Order" } }}
                  sx={{ minWidth: 200, "& .MuiInputBase-root": { minHeight: 40 } }}
                >
                  {ORDERS.map((x) => (
                    <MenuItem key={x.key} value={x.key}>
                      {x.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : undefined
          }
          loading={loadingData}
          error={loadError}
          onRetry={load}
          empty={{
            icon: <IconUsers size={32} stroke={1.5} />,
            title: "No customers yet.",
            hint: "People appear here when they create an account on the website.",
          }}
          pagination={
            query && !meta?.counts
              ? undefined
              : {
                  page,
                  rowsPerPage,
                  total,
                  onPageChange: setPage,
                  onRowsPerPageChange: (n) => {
                    setRowsPerPage(n);
                    setPage(0);
                  },
                }
          }
        />
      </Box>
      <CustomerPanel
        customer={panel}
        onClose={closeCustomer}
        onEdit={edit}
        onDelete={(c) => confirmDelete([c])}
        onToggle={(c, next) => setSignIn(c, next)}
        onTest={meta ? (c, next) => markTest([c], next) : undefined}
        busyToggle={!!panel && loadingEditdatatype === `${panel.id}-is_active`}
        busyTest={busyTest}
        canEdit={can("access_edit")}
        canDelete={can("access_delete")}
        canChats={can("access_read", "23")}
        canSupport={can("access_read", "25")}
      />
      {showModal ? (
        <DynamicModal
          show={showModal}
          setShow={setShowModal}
          faileds={fields}
          values={values}
          title={editMode ? `Edit ${nameOf(values)}` : "Add a customer"}
          editMode={editMode}
          description={editMode ? "Change their account details." : "They can sign in to the website with this email and password."}
          buttonTitle={editMode ? "Save changes" : "Add customer"}
          onSubmit={(form) => {
            dispatch(
              ManageClients({
                editMode,
                values: form,
                callback: (res) => {
                  if (res) {
                    setShowModal(false);
                    load();
                  }
                },
              }),
            );
          }}
          Schema={Schema}
        />
      ) : null}
    </PageContainer>
  );
};

export default Clients;
